/**
 * useMediaQuery — small reactive media-query hook.
 *
 * Returns `true` when the given CSS media-query string matches the current
 * viewport. Reactive: re-renders when the match state changes (window
 * resize, devtools breakpoint toggle, orientation change).
 *
 * SSR-safe: returns `false` until the first effect runs.
 *
 * Use the constant below so breakpoint values stay in sync with the rest of
 * the responsive logic.
 */
import { useEffect, useState } from 'react';

/**
 * Below-desktop breakpoint: the width at which the dashboards drop their
 * desktop shell and the bucket takes the whole viewport.
 *
 * Deliberately the same width as the `navtab` screen in tailwind.config.js, so
 * the chrome and the page it wraps change together and there is no band where
 * one has switched and the other has not. The two are declared separately
 * because they drive different mechanisms — CSS classes there, a component-tree
 * branch here — and CSS variables cannot be used in a media query. Keeping one
 * source of truth for the number is deferred work: change BOTH or neither.
 */
export const MOBILE_MAX = '(max-width: 1023px)';

export function useMediaQuery(query: string): boolean {
  const getMatch = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    const handler = (ev: MediaQueryListEvent) => setMatches(ev.matches);

    // Sync once on mount in case `getMatch()` ran before window was ready.
    setMatches(mql.matches);

    // `addEventListener('change', …)` is the standard MQL API; the
    // deprecated `addListener` fallback covers older Safari.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}
